// Ubicación en el repo: src/components/ProfessionalFormModal.jsx
// Modal Crear/Editar profesional (spec-prototipo.md 1.5, DECISIONS.md #16).
// Crear: pasa por invite-professional (Edge Function) — el Admin nunca
// asigna contraseña, solo dispara la invitación por correo.
// Editar: Admin puede tocar todo; `restricted` (auto-edición sin ser
// Admin, no usado todavía desde ningún botón de esta app, pero el
// componente ya lo soporta para cuando exista "Mi perfil") deja solo
// correo/teléfono — el backend lo refuerza igual vía trigger aunque la UI
// falle en ocultar algo.
import { useEffect, useState } from 'react';
import {
  ROLES,
  inviteProfessional,
  updateProfessional,
  findProfessionalByRut,
  sanitizeRut as sanitizeRutRpc,
  resendAccess,
  resetProfessionalMfa,
} from '../lib/professionals';
import {
  getSignedSignatureUrl,
  removeProfessionalSignature,
  uploadProfessionalSignature,
} from '../lib/professionalSignatures';

function emptyForm() {
  return {
    nombre: '',
    segundo_nombre: '',
    apellido: '',
    segundo_apellido: '',
    rut: '',
    role: '',
    email: '',
    phone: '',
    is_admin: false,
    branchIds: [],
  };
}

function professionalToForm(p) {
  return {
    nombre: p.nombre ?? '',
    segundo_nombre: p.segundo_nombre ?? '',
    apellido: p.apellido ?? '',
    segundo_apellido: p.segundo_apellido ?? '',
    rut: p.rut ?? '',
    role: p.role ?? '',
    email: p.email ?? '',
    phone: p.phone ?? '',
    is_admin: p.is_admin ?? false,
    branchIds: p.branch_ids ?? [],
  };
}

export default function ProfessionalFormModal({ professional, branches, restricted, onClose, onSaved }) {
  const isEdit = !!professional;
  const [form, setForm] = useState(() => (isEdit ? professionalToForm(professional) : emptyForm()));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [rutChecking, setRutChecking] = useState(false);
  const [rutDuplicate, setRutDuplicate] = useState(null);
  const [resending, setResending] = useState(false);
  const [resettingMfa, setResettingMfa] = useState(false);

  // Firma digital: mismo patrón que el logo de sucursal (BranchFormModal) —
  // `signatureFile` es lo elegido en este paso (todavía no subido);
  // `signaturePreviewUrl` es lo que se muestra (blob local del archivo
  // elegido, o la signed URL de la firma ya guardada si se está editando).
  const [signatureFile, setSignatureFile] = useState(null);
  const [signaturePreviewUrl, setSignaturePreviewUrl] = useState(null);
  const [removeSignature, setRemoveSignature] = useState(false);
  const [signatureError, setSignatureError] = useState('');

  useEffect(() => {
    setForm(isEdit ? professionalToForm(professional) : emptyForm());
    setRutDuplicate(null);
    setSignatureFile(null);
    setRemoveSignature(false);
    setSignatureError('');
    if (isEdit && professional.signature_path) {
      getSignedSignatureUrl(professional.signature_path)
        .then(setSignaturePreviewUrl)
        .catch(() => setSignaturePreviewUrl(null));
    } else {
      setSignaturePreviewUrl(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [professional]);

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  function toggleBranch(branchId) {
    setForm((f) => ({
      ...f,
      branchIds: f.branchIds.includes(branchId)
        ? f.branchIds.filter((id) => id !== branchId)
        : [...f.branchIds, branchId],
    }));
  }

  async function handleRutBlur() {
    if (restricted) return;
    setRutDuplicate(null);
    if (!form.rut.trim()) return;
    setRutChecking(true);
    try {
      const clean = await sanitizeRutRpc(form.rut);
      const existing = await findProfessionalByRut(clean, isEdit ? professional.id : undefined);
      setRutDuplicate(existing);
    } catch {
      // Si el chequeo previo falla por red, no bloqueamos — el índice
      // único del backend igual protege contra el duplicado real.
    } finally {
      setRutChecking(false);
    }
  }

  function handleSignatureChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setSignatureError('');
    setRemoveSignature(false);
    setSignatureFile(file);
    setSignaturePreviewUrl(URL.createObjectURL(file));
  }

  function handleRemoveSignature() {
    setSignatureFile(null);
    setSignaturePreviewUrl(null);
    setRemoveSignature(true);
  }

  async function handleResendAccess() {
    if (!isEdit) return;
    setResending(true);
    setError('');
    setInfo('');
    try {
      await resendAccess(professional.id);
      setInfo(`Se envió un correo de acceso a ${form.email}.`);
    } catch (err) {
      setError(err.message || 'No se pudo reenviar el acceso.');
    } finally {
      setResending(false);
    }
  }

  async function handleResetMfa() {
    if (!isEdit) return;
    const ok = window.confirm(
      `¿Resetear el 2FA de ${form.nombre}? Va a tener que enrolar un código nuevo en su próximo inicio de sesión.`
    );
    if (!ok) return;
    setResettingMfa(true);
    setError('');
    setInfo('');
    try {
      const result = await resetProfessionalMfa(professional.id);
      setInfo(
        result?.deleted
          ? 'Listo. Va a tener que enrolar un 2FA nuevo en su próximo login.'
          : 'Esta cuenta no tenía ningún 2FA activo.'
      );
    } catch (err) {
      setError(err.message || 'No se pudo resetear el 2FA.');
    } finally {
      setResettingMfa(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setInfo('');

    if (restricted) {
      setSaving(true);
      try {
        await updateProfessional(professional.id, {
          email: form.email.trim() || null,
          phone: form.phone.trim() || null,
        });
        onSaved();
      } catch (err) {
        setError(err.message || 'No se pudo guardar.');
      } finally {
        setSaving(false);
      }
      return;
    }

    if (!form.nombre.trim() || !form.apellido.trim()) {
      setError('Nombre y apellido son obligatorios.');
      return;
    }
    if (!form.role) {
      setError('Elige un rol.');
      return;
    }
    if (!form.email.trim()) {
      setError('El correo es obligatorio — será el usuario de acceso al sistema.');
      return;
    }
    if (rutDuplicate) {
      setError(`Ese RUT ya está registrado para ${rutDuplicate.nombre} ${rutDuplicate.apellido}.`);
      return;
    }

    setSaving(true);
    try {
      const values = {
        // Mayúsculas al guardar, no solo campos cortos identificadores
        // (nombre/apellido) — no email/rut/teléfono, ver PatientFormModal.
        nombre: form.nombre.trim().toUpperCase(),
        segundo_nombre: form.segundo_nombre.trim().toUpperCase() || null,
        apellido: form.apellido.trim().toUpperCase(),
        segundo_apellido: form.segundo_apellido.trim().toUpperCase() || null,
        rut: form.rut.trim() || null,
        role: form.role,
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        is_admin: form.is_admin,
      };

      let professionalId = professional?.id;
      if (isEdit) {
        await updateProfessional(professionalId, values, form.branchIds);
      } else {
        const result = await inviteProfessional(values, form.branchIds);
        professionalId = result?.id;
      }

      // La firma se maneja aparte de los demás campos porque la ruta de
      // Storage necesita el id del profesional, que recién existe acá para
      // el caso "nuevo profesional" (ver mismo patrón en BranchFormModal).
      if (professionalId) {
        if (signatureFile) {
          try {
            await uploadProfessionalSignature(professionalId, signatureFile, isEdit ? professional.signature_path : null);
          } catch (sigErr) {
            setSignatureError(sigErr.message || 'No se pudo subir la firma, pero el profesional se guardó.');
          }
        } else if (removeSignature && isEdit && professional.signature_path) {
          try {
            await removeProfessionalSignature(professionalId, professional.signature_path);
          } catch (sigErr) {
            setSignatureError(sigErr.message || 'No se pudo quitar la firma.');
          }
        }
      }

      onSaved();
    } catch (err) {
      if (err.code === '23505') {
        setError('Ese RUT ya está registrado en otro profesional.');
      } else {
        setError(err.message || 'No se pudo guardar el profesional.');
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal-card modal-card--wide">
        <h2>{isEdit ? 'Editar profesional' : 'Nuevo profesional'}</h2>
        {restricted && (
          <p className="field-hint">
            Solo puedes editar tu correo y teléfono. Para cambiar otros datos, pide a un Admin.
          </p>
        )}
        {!isEdit && (
          <p className="field-hint">
            Se enviará un correo de invitación — el profesional define su propia contraseña, tú nunca la ves.
          </p>
        )}

        <form onSubmit={handleSubmit} className="patient-form">
          <div className="form-row">
            <label className="field">
              <span>Nombre *</span>
              <input
                value={form.nombre}
                onChange={(e) => set('nombre', e.target.value)}
                disabled={restricted}
                required
              />
            </label>
            <label className="field">
              <span>Segundo nombre</span>
              <input
                value={form.segundo_nombre}
                onChange={(e) => set('segundo_nombre', e.target.value)}
                disabled={restricted}
              />
            </label>
          </div>

          <div className="form-row">
            <label className="field">
              <span>Apellido *</span>
              <input
                value={form.apellido}
                onChange={(e) => set('apellido', e.target.value)}
                disabled={restricted}
                required
              />
            </label>
            <label className="field">
              <span>Segundo apellido</span>
              <input
                value={form.segundo_apellido}
                onChange={(e) => set('segundo_apellido', e.target.value)}
                disabled={restricted}
              />
            </label>
          </div>

          <div className="form-row">
            <label className="field">
              <span>RUT</span>
              <input
                value={form.rut}
                onChange={(e) => set('rut', e.target.value)}
                onBlur={handleRutBlur}
                disabled={restricted}
              />
              {rutChecking && <span className="field-hint">Revisando…</span>}
              {rutDuplicate && (
                <span className="field-hint field-hint--error">
                  Ya registrado: {rutDuplicate.nombre} {rutDuplicate.apellido}
                </span>
              )}
            </label>
            <label className="field">
              <span>Rol *</span>
              <select value={form.role} onChange={(e) => set('role', e.target.value)} disabled={restricted} required>
                <option value="">—</option>
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="form-row">
            <label className="field">
              <span>Correo * {isEdit ? '' : '(será el usuario de acceso)'}</span>
              <input
                type="email"
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
                required
              />
            </label>
            <label className="field">
              <span>Teléfono</span>
              <input value={form.phone} onChange={(e) => set('phone', e.target.value)} />
            </label>
          </div>

          {!restricted && (
            <div className="field">
              <span>Sucursal(es)</span>
              <div className="checkbox-group">
                {branches.map((b) => (
                  <label key={b.id} className="checkbox-inline">
                    <input
                      type="checkbox"
                      checked={form.branchIds.includes(b.id)}
                      onChange={() => toggleBranch(b.id)}
                    />
                    {b.name}
                  </label>
                ))}
              </div>
              <span className="field-hint">Sin ninguna marcada = acceso a todas las sucursales.</span>
            </div>
          )}

          {!restricted && (
            <label className="checkbox-inline">
              <input type="checkbox" checked={form.is_admin} onChange={(e) => set('is_admin', e.target.checked)} />
              Es Admin (gestiona profesionales, sucursales y configuración)
            </label>
          )}

          {/* `div`, no `label` — igual que el logo de sucursal en BranchFormModal:
              este campo tiene un botón ("Quitar firma") además del input de
              archivo, y envolver todo en un <label> haría que el clic en el
              botón dispare también un clic sintético en el input. */}
          {!restricted && (
            <div className="field">
              <span>Firma digital (opcional)</span>
              {signaturePreviewUrl && (
                <img
                  src={signaturePreviewUrl}
                  alt="Firma digital"
                  style={{ maxHeight: 64, maxWidth: 220, objectFit: 'contain', marginBottom: 6, display: 'block' }}
                />
              )}
              <input type="file" accept="image/png,image/jpeg,image/webp" onChange={handleSignatureChange} />
              <span className="field-hint">
                Si la subes, reemplaza por completo el nombre/RUT/línea de firma manual en los documentos
                impresos (audiometría, receta) — se muestra solo la imagen, así que si necesitás que se vea el
                nombre o RUT del profesional, la imagen ya debe incluirlos (ej. un timbre escaneado). Sin firma
                cargada, se imprime el formato de siempre: nombre, RUT y línea en blanco para firmar a mano.
                Máximo 1 MB — idealmente PNG con fondo transparente.
              </span>
              {signaturePreviewUrl && (
                <button
                  type="button"
                  className="link-button link-button--danger"
                  onClick={handleRemoveSignature}
                  style={{ marginTop: 4 }}
                >
                  Quitar firma
                </button>
              )}
              {signatureError && (
                <span className="field-hint" style={{ color: 'var(--danger, #c0392b)' }}>
                  {signatureError}
                </span>
              )}
            </div>
          )}

          {isEdit && !restricted && (
            <div className="field">
              <button type="button" className="link-button" onClick={handleResendAccess} disabled={resending}>
                {resending ? 'Enviando…' : '✉️ Reenviar acceso (invitación o restablecer contraseña)'}
              </button>
            </div>
          )}

          {isEdit && !restricted && form.role && (
            <div className="field">
              <button type="button" className="link-button" onClick={handleResetMfa} disabled={resettingMfa}>
                {resettingMfa ? 'Reseteando…' : '🔐 Resetear 2FA (perdió su teléfono)'}
              </button>
            </div>
          )}

          {info && <p className="field-hint">{info}</p>}
          {error && <p className="auth-error">{error}</p>}

          <div className="modal-actions">
            <button type="button" className="button-secondary" onClick={onClose} disabled={saving}>
              Cancelar
            </button>
            <button type="submit" className="button-primary" disabled={saving || rutChecking}>
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
